import { useState } from "react";
import axiosClient from "../../api/axiosClient";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import LoadingSpinner from "../../components/LoadingSpinner";
import "./Auth.css";
import "./ForgotPassword.css";

/* =====================================================
   FORGOT PASSWORD — 3-step flow
   Step 1: Enter email → send OTP
   Step 2: Enter OTP
   Step 3: Enter new password
===================================================== */
function ForgotPassword() {
  const navigate = useNavigate();

  const [step, setStep] = useState(1); // 1 | 2 | 3
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  /* ---------- Step 1: Send OTP ---------- */
  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Please enter your email");
    setLoading(true);
    try {
      await axiosClient.post("/auth/forgot-password", { email });
      toast.success("OTP sent to your email!");
      setStep(2);
      startResendTimer();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  /* ---------- Resend OTP countdown ---------- */
  const startResendTimer = () => {
    setResendTimer(60);
    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    setLoading(true);
    try {
      await axiosClient.post("/auth/forgot-password", { email });
      toast.success("OTP resent!");
      startResendTimer();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  /* ---------- Step 2: Verify OTP ---------- */
  const handleVerifyOTP = (e) => {
    e.preventDefault();
    if (otp.length !== 6) return toast.error("Enter the 6-digit OTP");
    setStep(3);
  };

  /* ---------- Step 3: Reset Password ---------- */
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) return toast.error("Password must be at least 6 characters");
    if (newPassword !== confirmPassword) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await axiosClient.post("/auth/reset-password", { email, otp, newPassword });
      toast.success("Password reset successfully! Please login.");
      navigate("/login");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to reset password");
      // If OTP is wrong/expired go back to step 2
      if (err.response?.status === 400) setStep(2);
    } finally {
      setLoading(false);
    }
  };

  const spinnerMsg = step === 1 ? "Sending OTP" : "Resetting Password";
  const spinnerSub = step === 1 ? "We're sending a code to your email…" : "Updating your password securely…";

  /* ---------- Render ---------- */
  return (
    <div className="auth-page">
      {loading && <LoadingSpinner overlay message={spinnerMsg} sub={spinnerSub} />}
      <div className="auth-container fp-container">

        {/* ── Progress indicator ── */}
        <div className="fp-progress">
          {[1, 2, 3].map((s) => (
            <div key={s} className={`fp-step ${step >= s ? "active" : ""} ${step > s ? "done" : ""}`}>
              <div className="fp-step-circle">
                {step > s ? "✓" : s}
              </div>
              <span className="fp-step-label">
                {s === 1 ? "Email" : s === 2 ? "OTP" : "New Password"}
              </span>
            </div>
          ))}
        </div>

        {/* ════════ STEP 1 — Email ════════ */}
        {step === 1 && (
          <>
            <div className="auth-header">
              <h2>Forgot <span>Password?</span></h2>
              <p className="fp-subtitle">Enter your registered email and we'll send you an OTP.</p>
            </div>

            <form onSubmit={handleSendOTP} className="auth-form">
              <input
                type="email"
                className="auth-input"
                placeholder="Registered Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <button className="auth-submit" disabled={loading}>
                {loading ? "Sending OTP..." : "Send OTP"}
              </button>
            </form>

            <p className="auth-back" onClick={() => navigate("/login")}>
              ← Back to Login
            </p>
          </>
        )}

        {/* ════════ STEP 2 — OTP ════════ */}
        {step === 2 && (
          <>
            <div className="auth-header">
              <h2>Enter <span>OTP</span></h2>
              <p className="fp-subtitle">
                A 6-digit OTP was sent to <strong>{email}</strong>
              </p>
            </div>

            <form onSubmit={handleVerifyOTP} className="auth-form">
              <input
                type="text"
                className="auth-input fp-otp-input"
                placeholder="6-digit OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                maxLength={6}
                inputMode="numeric"
                required
              />
              <button className="auth-submit" disabled={loading}>
                Verify OTP
              </button>
            </form>

            <div className="auth-resend-container">
              {resendTimer > 0 ? (
                <span>Resend OTP in <strong>{resendTimer}s</strong></span>
              ) : (
                <button
                  className="auth-resend-btn"
                  onClick={handleResendOTP}
                  disabled={loading}
                >
                  Resend OTP
                </button>
              )}
            </div>

            <p className="auth-back" onClick={() => setStep(1)}>
              ← Change Email
            </p>
          </>
        )}

        {/* ════════ STEP 3 — New Password ════════ */}
        {step === 3 && (
          <>
            <div className="auth-header">
              <h2>New <span>Password</span></h2>
              <p className="fp-subtitle">Choose a strong password for your account.</p>
            </div>

            <form onSubmit={handleResetPassword} className="auth-form">
              <input
                type="password"
                className="auth-input"
                placeholder="New Password (min 6 chars)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
              <input
                type="password"
                className="auth-input"
                placeholder="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              <button className="auth-submit" disabled={loading}>
                {loading ? "Resetting..." : "Reset Password"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default ForgotPassword;
